import React from 'react';
import { usePos } from '../PosContext';
import { HandPlatter, DollarSign, Trash2, Split, MessageSquare, Printer, ArrowRightLeft, Receipt, Clock, CheckCircle2 } from 'lucide-react';
import FulfillmentSelector from '../../../components/FulfillmentSelector';
import { CustomerFields } from '../../../features/orders/CustomerFields';
import { ItemRow } from '../../../components/POS/ItemRow';
import { OrderSummaryFooter } from '../../../components/POS/OrderSummaryFooter';
import { SplitOrderModal } from '../../../components/POS/SplitOrderModal';
import { ConfirmCancelOrderModal } from '../../../features/orders/components/ConfirmCancelOrderModal';
import { TransferTableModal } from '../../../components/POS/TransferTableModal';
import type { Order, FulfillmentType, ServeMode } from '../../../types/@types/order';
import type { Table } from '../../../types/@types/table';
import { formatCurrency } from '../../../utils/format-currency';
import { QuickNoteChips } from '../../../components/POS/QuickNoteChips';
import VoiceDictationButton from '../../../components/ui/VoiceDictationButton';

function formatPrintTime(dateStr?: string | null): string {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit', hour12: true });
  } catch {
    return '';
  }
}

function formatFullDateTime(dateStr?: string | null): string {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleString('es-CO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  } catch {
    return '';
  }
}

interface CartItem {
  product: any;
  qty: number;
  flavors?: { id: string; name: string; qty: number }[];
  additions?: Record<string, number>;
  exceptions?: string[];
  linePriceCalc?: any;
  kitchenSummary?: string;
  serveMode: ServeMode;
}

export interface CartPanelProps {
  isTakeawayMode: boolean;
  selected: Table | null;
  orderCustomer: { customerName: string; customerPhone: string };
  setSelected: (val: Table | null) => void;
  hasActiveOrder: boolean;
  activeOrder: Order | null;
  draftFulfillment: FulfillmentType;
  handleOrderFulfillmentChange: (next: FulfillmentType) => void;
  orderLocked: boolean;
  previousOrdersWithItems: Array<{ order: Order; items: any[]; totals: any; sequence: number }>;
  ORDER_STATUS_LABELS: Record<string, string>;
  buildDetailContentFromOrderItem: (item: any) => React.ReactNode;
  activeOrderSequence: number | null;
  setSplitModalOpen: (val: boolean) => void;
  splittingOrder: boolean;
  setCancelModalOpen: (val: boolean) => void;
  cancelOrderIsPending: boolean;
  ordersByTable: Order[] | undefined;
  setActiveOrderId: (id: string | number | null) => void;
  activeOrderId: string | number | null;
  activeOrderItems: any[];
  ITEM_STATUS_LABELS: Record<string, string>;
  ITEM_STATUS_CLASSES: Record<string, string>;
  openOrderItem: string | null;
  setOpenOrderItem: (id: string | null) => void;
  handleActiveItemFulfillment: (id: string, mode: FulfillmentType | null) => void;
  totals: { subtotal: number; total: number } | null;
  handleSendPending: () => void;
  hasPendingItems: boolean;
  sendToKitchenMutationIsPending: boolean;
  pendingItemsCount: number;
  handlePayClick: () => void;
  hasDraft: boolean;
  nextOrderSequence: number;
  highestKnownSequence: number;
  cart: Record<string, CartItem>;
  handleCartItemServeMode: (key: string, mode: ServeMode) => void;
  setOptionsKey: (key: string) => void;
  buildDetailContentFromCartItem: (item: CartItem) => React.ReactNode;
  changeQty: (key: string, delta: number) => void;
  removeCartItem: (key: string) => void;
  setCart: (val: any) => void;
  handleOrder: () => void;
  hasFlavorErrors: boolean;
}

export function CartPanel() {
  const {
    isTakeawayMode, selectedTable: selected, orderCustomer, setSelectedTable: setSelected,
    hasActiveOrder, activeOrder, draftFulfillment, handleOrderFulfillmentChange,
    orderLocked, previousOrdersWithItems, activeOrderSequence,
    splittingOrder, cancelOrderIsPending, ordersByTable, sortedTableOrders, activeOrderAccountIndex,
    setActiveOrderId, activeOrderId, activeOrderItems,
    totals, handleSendPending, sendToKitchenMutationIsPending,
    pendingItemsCount, hasPendingItems,
    handlePayClick, hasDraft, nextOrderSequence, highestKnownSequence,
    cart, handleCartItemServeMode, setOptionsKey,
    buildDetailContentFromCartItem, buildDetailContentFromOrderItem,
    changeQty, removeCartItem, setCart,
    handleOrder, hasFlavorErrors,
    createOrderIsPending, handleActiveItemFulfillment, handleUpdateActiveItemNote,
    lastReceipt, setReceiptToPrint,
    splitOrderMutation, cancelOrderMutation, handleCancelActiveOrder,
    removeActiveOrderItem, removingItem, printOrderTicket, printPreReceipt,
    transferOrderTableMutation, tables,
  } = usePos();

  // Mapping constant labels that were probably passed as props
  const ORDER_STATUS_LABELS: any = { WAITING: 'En espera', IN_PRODUCTION: 'En cocina', DONE: 'Listo', DELIVERED: 'Entregado', PAID: 'Pagado', CANCELLED: 'Cancelado' };
  const ITEM_STATUS_LABELS: any = { PENDING: 'Pendiente', WAITING: 'En cola', SENT_TO_KITCHEN: 'En cocina', IN_PRODUCTION: 'Preparando', DONE: 'Listo', DELIVERED: 'Entregado', CANCELLED: 'Cancelado' };
  const ITEM_STATUS_CLASSES: any = { PENDING: 'bg-yellow-100 text-yellow-700', WAITING: 'bg-orange-100 text-orange-700', SENT_TO_KITCHEN: 'bg-blue-100 text-blue-700', IN_PRODUCTION: 'bg-purple-100 text-purple-700', DONE: 'bg-green-100 text-green-700', DELIVERED: 'bg-teal-100 text-teal-700', CANCELLED: 'bg-red-100 text-red-700' };

  const [splitModalOpen, setSplitModalOpen] = React.useState(false);
  const [cancelModalOpen, setCancelModalOpen] = React.useState(false);
  const [transferModalOpen, setTransferModalOpen] = React.useState(false);
  const [openOrderItem, setOpenOrderItem] = React.useState<string | null>(null);
  const [openNoteKey, setOpenNoteKey] = React.useState<string | null>(null);
  const [editingNoteValue, setEditingNoteValue] = React.useState<string>('');

  const setItemNote = (key: string, note: string) => {
    setCart((prev: any) => ({
      ...prev,
      [key]: { ...prev[key], note },
    }));
  };

  const trimmedCustomerName = orderCustomer.customerName.trim();

  const totalItemsCount = React.useMemo(() => {
    if (hasActiveOrder) {
      return activeOrderItems.reduce((acc: number, item: any) => acc + (item.quantity || 0), 0);
    }
    return Object.values(cart).reduce((acc: number, item: any) => acc + (item.qty || 0), 0);
  }, [hasActiveOrder, activeOrderItems, cart]);

  const tableOrders = React.useMemo(() => {
    return sortedTableOrders || [];
  }, [sortedTableOrders]);

  const isComandaPrinted = Boolean(activeOrder?.comandaPrinted || (activeOrder as any)?.comanda_printed);
  const comandaPrintedRaw = activeOrder?.comandaPrintedAt || (activeOrder as any)?.comanda_printed_at;
  const comandaPrintedTime = React.useMemo(() => formatPrintTime(comandaPrintedRaw), [comandaPrintedRaw]);
  const comandaPrintedFull = React.useMemo(() => formatFullDateTime(comandaPrintedRaw), [comandaPrintedRaw]);

  const orderCreatedRaw = activeOrder?.createdAt || (activeOrder as any)?.created_at;
  const orderCreatedTime = React.useMemo(() => formatPrintTime(orderCreatedRaw), [orderCreatedRaw]);
  const orderCreatedFull = React.useMemo(() => formatFullDateTime(orderCreatedRaw), [orderCreatedRaw]);

  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-2xl border border-divider/70 bg-gradient-to-b from-slate-50/70 to-slate-100/50 dark:from-neutral-800/40 dark:to-neutral-900/30 backdrop-blur-sm px-4.5 py-4 shadow-sm">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-base font-bold text-[color:var(--color-text)] tracking-tight flex items-center gap-2">
              {isTakeawayMode ? 'Pedido para llevar' : `Mesa #${selected?.number}`}
              {totalItemsCount > 0 && (
                <span key={totalItemsCount} className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-emerald-500 px-1 text-[10px] font-extrabold text-white animate-pop">
                  {totalItemsCount}
                </span>
              )}
            </p>
            {isTakeawayMode ? (
              trimmedCustomerName ? (
                <p className="text-xs font-medium text-slate-500 dark:text-neutral-400">Cliente: {trimmedCustomerName}</p>
              ) : null
            ) : (
              <p className="text-xs font-medium text-slate-500 dark:text-neutral-400">ID de mesa: {selected?.id}</p>
            )}
          </div>
          {isTakeawayMode ? (
            <span className="inline-flex min-h-[2rem] items-center rounded-full bg-blue-100/85 dark:bg-blue-950/40 px-3.5 text-xs font-semibold text-blue-700 dark:text-blue-400 border border-blue-200/50">
              TAKEAWAY
              {trimmedCustomerName ? ` • ${trimmedCustomerName}` : ''}
            </span>
          ) : (
            <div className="flex gap-1.5">
              {hasActiveOrder && (
                <button
                  onClick={() => setTransferModalOpen(true)}
                  className="inline-flex h-9 items-center justify-center rounded-full border border-primary/30 bg-primary/5 px-2.5 text-xs font-bold text-primary transition hover:bg-primary/10 hover:border-primary/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/70 active:scale-[0.98]"
                  aria-label="Transferir de mesa"
                >
                  <ArrowRightLeft className="mr-1 h-3 w-3" />
                  Transferir
                </button>
              )}
              <button
                onClick={() => setSelected(null)}
                className="inline-flex h-9 items-center justify-center rounded-full border border-divider bg-surface px-3.5 text-xs font-bold text-slate-700 dark:text-neutral-300 transition hover:bg-surface-muted hover:border-slate-300 dark:hover:border-neutral-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-accent)]/70 active:scale-[0.98]"
                aria-label="Cambiar mesa"
              >
                Cambiar mesa
              </button>
            </div>
          )}
        </div>
        <div className="mt-4 pt-3.5 border-t border-divider/60 flex items-center justify-between gap-3">
          <span className="text-xs font-semibold text-slate-500 dark:text-neutral-400">Modo de entrega</span>
          <FulfillmentSelector
            value={
              hasActiveOrder && activeOrder
                ? (activeOrder.fulfillmentType ?? 'DINE_IN')
                : draftFulfillment
            }
            onChange={handleOrderFulfillmentChange}
            disabled={hasActiveOrder ? orderLocked : false}
            compact
          />
        </div>
        <CustomerFields forceShow={
          (hasActiveOrder && activeOrder ? activeOrder.fulfillmentType : draftFulfillment) === 'TAKEOUT'
        } />
      </div>

      {hasActiveOrder ? (
        <div className="flex flex-col gap-3">
          {/* Selector de Cuentas (Tabs) si la mesa tiene múltiples cuentas divididas */}
          {tableOrders.length > 1 && (
            <div className="flex flex-col gap-1.5 rounded-2xl bg-slate-100/90 dark:bg-neutral-850/90 p-2 border border-divider/70 shadow-sm">
              <div className="flex items-center justify-between px-1.5 pt-0.5">
                <span className="text-[11px] font-extrabold uppercase tracking-wider text-slate-500 dark:text-neutral-400">
                  Cuentas de la mesa ({tableOrders.length})
                </span>
                <span className="text-[10px] font-semibold text-text-muted">
                  Toca para seleccionar
                </span>
              </div>
              <div className="grid grid-cols-2 gap-1.5">
                {tableOrders.map((order, idx) => {
                  const isSelected = String(activeOrderId) === String(order.id);
                  const accountNum = idx + 1;
                  const orderItems = order.items || [];
                  const itemsCount = orderItems.reduce((sum: number, it: any) => sum + (it.quantity || 1), 0);
                  const orderTotal = Number(order.total) || orderItems.reduce((sum: number, it: any) => sum + (Number(it.line_subtotal) || (Number(it.unitPrice || it.base_price || 0) * (it.quantity || 1))), 0);

                  return (
                    <button
                      key={order.id}
                      type="button"
                      onClick={() => setActiveOrderId(order.id)}
                      className={`flex flex-col items-start justify-center p-2.5 rounded-xl transition-all duration-200 border text-left ${
                        isSelected
                          ? 'bg-gradient-to-r from-emerald-600 to-teal-650 text-white border-emerald-500 shadow-md shadow-emerald-600/20 ring-2 ring-emerald-500/30'
                          : 'bg-white dark:bg-neutral-800 text-slate-700 dark:text-neutral-200 border-border/80 hover:bg-slate-50 dark:hover:bg-neutral-750 hover:border-slate-300'
                      }`}
                    >
                      <div className="flex items-center justify-between w-full gap-1">
                        <span className="font-extrabold text-xs tracking-tight">
                          Cuenta {accountNum}
                        </span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
                          isSelected
                            ? 'bg-white/20 text-white'
                            : 'bg-slate-150 dark:bg-neutral-700 text-slate-600 dark:text-neutral-300'
                        }`}>
                          {itemsCount} {itemsCount === 1 ? 'ítem' : 'ítems'}
                        </span>
                      </div>
                      <span className={`text-xs font-black mt-1 ${isSelected ? 'text-white' : 'text-emerald-600 dark:text-emerald-450'}`}>
                        {formatCurrency(orderTotal)}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {previousOrdersWithItems.length > 0 && (
            <div className="space-y-3">
              {previousOrdersWithItems.map(({ order, items, totals: previousTotals, sequence }) => (
                <article
                  key={order.id}
                  className="rounded-2xl border border-divider bg-surface-muted px-4 py-3 shadow-sm"
                >
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm font-semibold text-[color:var(--color-text)]">Orden #{sequence}</p>
                    <span className="inline-flex min-h-[1.5rem] items-center rounded-full bg-surface px-3 text-[10px] font-semibold text-muted">
                      {ORDER_STATUS_LABELS[order.status] ?? order.status}
                    </span>
                  </div>
                  <ul className="mt-2 divide-y divide-[color:var(--color-border)]/60">
                    {items.map((item: any, index: number) => (
                      <li key={item.id ?? `${order.id}-${index}`} className="py-2">
                        <ItemRow
                          id={item.id ?? `${order.id}-${index}`}
                          quantity={item.quantity}
                          name={item.name || item.product?.name || 'Producto'}
                          unitPrice={item.unitPrice}
                          detailContent={buildDetailContentFromOrderItem(item)}
                        />
                      </li>
                    ))}
                  </ul>
                  <OrderSummaryFooter
                    subtotal={previousTotals.subtotal}
                    total={previousTotals.total}
                    className="mt-3"
                  />
                </article>
              ))}
            </div>
          )}

          <section className="space-y-3 rounded-2xl border border-divider bg-surface px-4 py-4 shadow-sm">
            {/* Header: Title, Comanda Status & Order Status */}
            <div className="flex items-start justify-between gap-2">
              <div className="flex flex-col min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-sm font-bold text-[color:var(--color-text)] truncate">
                    {tableOrders.length > 1
                      ? `Cuenta ${activeOrderAccountIndex || 1}`
                      : (activeOrder?.orderSequence || (activeOrder as any)?.order_sequence)
                        ? `Orden #${activeOrder?.orderSequence || (activeOrder as any)?.order_sequence}`
                        : 'Orden activa'}
                  </h3>
                  {activeOrder && (
                    <span
                      className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold border ${
                        isComandaPrinted
                          ? 'bg-emerald-50 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-300 border-emerald-300 dark:border-emerald-800'
                          : 'bg-amber-50 dark:bg-amber-950/50 text-amber-700 dark:text-amber-300 border-amber-300 dark:border-amber-800'
                      }`}
                      title={
                        isComandaPrinted
                          ? (comandaPrintedFull ? `Comanda impresa: ${comandaPrintedFull}` : 'Comanda impresa')
                          : 'Comanda no impresa aún'
                      }
                    >
                      {isComandaPrinted ? (
                        <>
                          <CheckCircle2 size={11} className="text-emerald-600 dark:text-emerald-400 shrink-0" />
                          <span>Impresa{comandaPrintedTime ? ` ${comandaPrintedTime}` : ''}</span>
                        </>
                      ) : (
                        <>
                          <Clock size={11} className="text-amber-600 dark:text-amber-400 shrink-0" />
                          <span>Sin comanda</span>
                        </>
                      )}
                    </span>
                  )}
                </div>

                {/* Tiempos de creación de pedido y de impresión de comanda */}
                {activeOrder && (
                  <div className="flex items-center gap-2.5 text-[11px] text-text-muted mt-1 flex-wrap">
                    {orderCreatedTime && (
                      <span className="inline-flex items-center gap-1" title={`Hora del pedido: ${orderCreatedFull}`}>
                        <Clock size={11} className="text-slate-400 shrink-0" />
                        <span>Pedido: <strong className="font-semibold text-slate-700 dark:text-neutral-300">{orderCreatedTime}</strong></span>
                      </span>
                    )}
                    {isComandaPrinted && comandaPrintedTime ? (
                      <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400 font-medium" title={`Impreso en cocina: ${comandaPrintedFull}`}>
                        <Printer size={11} className="text-emerald-600 dark:text-emerald-400 shrink-0" />
                        <span>Comanda: <strong className="font-bold">{comandaPrintedTime}</strong></span>
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400 font-medium">
                        <Printer size={11} className="text-amber-500 shrink-0" />
                        <span>Comanda: Pendiente</span>
                      </span>
                    )}
                  </div>
                )}
              </div>

              {activeOrder ? (
                <span className="inline-flex shrink-0 min-h-[1.5rem] items-center rounded-full bg-surface-muted px-2.5 text-[10px] font-semibold text-muted border border-divider/50">
                  {ORDER_STATUS_LABELS[activeOrder.status.toUpperCase()] ?? activeOrder.status}
                </span>
              ) : (
                <div className="h-5 w-16 rounded-full bg-muted animate-pulse shrink-0" />
              )}
            </div>

            {/* Action buttons toolbar: Comanda, Cuenta, Dividir, Cancelar */}
            {activeOrder ? (
              <div className="grid grid-cols-4 gap-1.5 pt-0.5">
                <button
                  type="button"
                  onClick={() => printOrderTicket()}
                  className={`min-w-0 inline-flex h-9 items-center justify-center gap-1 rounded-xl border px-1 text-xs font-semibold transition-all duration-200 active:scale-[0.98] ${
                    isComandaPrinted
                      ? 'border-divider text-[color:var(--color-text)] hover:text-blue-600 hover:bg-blue-50 hover:border-blue-200 dark:hover:text-blue-400 dark:hover:bg-blue-950/20 dark:hover:border-blue-900/35'
                      : 'border-amber-400 bg-amber-50/80 text-amber-900 hover:bg-amber-100 hover:border-amber-500 dark:bg-amber-950/40 dark:border-amber-600 dark:text-amber-200'
                  }`}
                  aria-label="Imprimir comanda"
                  title={
                    isComandaPrinted
                      ? (comandaPrintedTime ? `Comanda ya impresa a las ${comandaPrintedTime}. Clic para reimprimir.` : 'Comanda ya impresa. Clic para reimprimir.')
                      : 'Comanda no impresa. Clic para imprimir.'
                  }
                >
                  <Printer size={15} className="shrink-0" aria-hidden="true" />
                  <span className="truncate">
                    {isComandaPrinted ? 'Reimprimir' : 'Comanda'}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => printPreReceipt()}
                  disabled={activeOrderItems.length === 0 || !activeOrder || activeOrder.status === 'CANCELLED'}
                  className="min-w-0 inline-flex h-9 items-center justify-center gap-1 rounded-xl border border-sky-300 dark:border-sky-800 bg-sky-50 dark:bg-sky-950/30 px-1 text-xs font-semibold text-sky-700 dark:text-sky-300 transition-all duration-200 hover:bg-sky-100 hover:border-sky-400 dark:hover:bg-sky-900/40 disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98]"
                  aria-label="Imprimir pre-cuenta"
                  title="Imprimir pre-cuenta para llevar a la mesa"
                >
                  <Receipt size={15} className="shrink-0" aria-hidden="true" />
                  <span className="truncate">Cuenta</span>
                </button>
                <button
                  type="button"
                  onClick={() => setSplitModalOpen(true)}
                  disabled={splittingOrder}
                  className="min-w-0 inline-flex h-9 items-center justify-center gap-1 rounded-xl border border-divider px-1 text-xs font-semibold text-[color:var(--color-text)] transition-all duration-200 hover:text-indigo-650 hover:bg-indigo-50 hover:border-indigo-200 dark:hover:text-indigo-400 dark:hover:bg-indigo-950/20 dark:hover:border-indigo-900/35 disabled:cursor-not-allowed disabled:opacity-60 active:scale-[0.98]"
                  aria-label="Dividir cuenta"
                  title="Dividir cuenta"
                >
                  <Split size={15} className="shrink-0" aria-hidden="true" />
                  <span className="truncate">Dividir</span>
                </button>
                <button
                  type="button"
                  onClick={() => setCancelModalOpen(true)}
                  disabled={cancelOrderIsPending}
                  className="min-w-0 inline-flex h-9 items-center justify-center gap-1 rounded-xl border border-divider px-1 text-xs font-semibold text-[color:var(--color-text)] transition-all duration-200 hover:text-red-650 hover:bg-red-50 hover:border-red-200 dark:hover:text-red-400 dark:hover:bg-red-950/20 dark:hover:border-red-900/35 disabled:cursor-not-allowed disabled:opacity-60 active:scale-[0.98]"
                  aria-label="Cancelar pedido"
                  title="Cancelar pedido"
                >
                  <Trash2 size={15} className="shrink-0" aria-hidden="true" />
                  <span className="truncate">Cancelar</span>
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-4 gap-1.5 pt-0.5 animate-pulse">
                <div className="h-9 rounded-xl bg-muted" />
                <div className="h-9 rounded-xl bg-muted" />
                <div className="h-9 rounded-xl bg-muted" />
                <div className="h-9 rounded-xl bg-muted" />
              </div>
            )}
            <ul className="divide-y divide-[color:var(--color-border)]/60" role="list">
              {activeOrderItems.map((item: any) => {
                const eff =
                  item.fulfillmentTypeOverride ??
                  (activeOrder?.fulfillmentType ?? 'DINE_IN');
                const statusKey =
                  typeof item.status === 'string'
                    ? item.status.toUpperCase()
                    : '';
                const statusLabel = statusKey
                  ? ITEM_STATUS_LABELS[statusKey] ?? item.rawStatus ?? statusKey
                  : typeof item.rawStatus === 'string'
                    ? item.rawStatus
                    : '';
                const statusClass = statusKey
                  ? ITEM_STATUS_CLASSES[statusKey] ?? 'bg-gray-200 text-gray-600'
                  : 'bg-gray-200 text-gray-600';
                return (
                  <li key={item.id} className="py-1.5 px-2 -mx-2 hover:bg-slate-50/70 dark:hover:bg-neutral-800/20 rounded-2xl transition-all duration-200">
                    <ItemRow
                      id={item.id}
                      quantity={item.quantity}
                      name={item.name || item.product?.name || 'Producto'}
                      unitPrice={item.unitPrice}
                      meta={[
                        statusLabel ? (
                          <span
                            key="status"
                            className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${statusClass}`}
                          >
                            {statusLabel}
                          </span>
                        ) : null,
                        (
                          <div key="mode" className="relative">
                            <button
                              type="button"
                              disabled={orderLocked}
                              onClick={() =>
                                orderLocked
                                  ? undefined
                                  : openOrderItem === item.id
                                    ? setOpenOrderItem(null)
                                    : setOpenOrderItem(item.id)
                              }
                              className="inline-flex items-center rounded-full border border-divider px-3 py-1 text-[11px] font-medium text-[color:var(--color-text)] transition hover:bg-surface-muted disabled:opacity-50"
                              aria-label="Cambiar modo de servicio"
                              aria-expanded={!orderLocked && openOrderItem === item.id}
                            >
                              {eff === 'TAKEOUT' || eff === 'TAKEAWAY' ? 'Para llevar' : 'En mesa'}
                            </button>
                            {!orderLocked && openOrderItem === item.id && (
                              <div className="absolute left-0 top-full z-20 mt-2 w-48 rounded-2xl border border-divider bg-surface text-xs shadow-elevated">
                                <button
                                  className="block w-full px-4 py-2 text-left hover:bg-surface-muted"
                                  onClick={() => {
                                    handleActiveItemFulfillment(item.id, 'TAKEOUT')
                                    setOpenOrderItem(null)
                                  }}
                                >
                                  Marcar como Para llevar
                                </button>
                                <button
                                  className="block w-full px-4 py-2 text-left hover:bg-surface-muted"
                                  onClick={() => {
                                    handleActiveItemFulfillment(item.id, 'DINE_IN')
                                    setOpenOrderItem(null)
                                  }}
                                >
                                  Marcar como En mesa
                                </button>
                                <button
                                  className="block w-full px-4 py-2 text-left hover:bg-surface-muted"
                                  onClick={() => {
                                    handleActiveItemFulfillment(item.id, null)
                                    setOpenOrderItem(null)
                                  }}
                                >
                                  Usar el de la orden
                                </button>
                              </div>
                            )}
                          </div>
                        ),
                        (!orderLocked && activeOrder?.payment_status !== 'PAID') ? (
                          <button
                            key="remove"
                            type="button"
                            disabled={removingItem}
                            onClick={() => {
                              if (confirm(`¿Quitar "${item.name || item.product?.name || 'este producto'}" del pedido?`)) {
                                removeActiveOrderItem(item.id);
                              }
                            }}
                            className="inline-flex items-center gap-1 rounded-full border border-red-200 px-2.5 py-1 text-[11px] font-medium text-red-600 transition hover:bg-red-50 disabled:opacity-50"
                            aria-label="Quitar producto del pedido"
                            title="Quitar del pedido"
                          >
                            <Trash2 size={13} /> Quitar
                          </button>
                        ) : null,
                      ].filter(Boolean)}
                      detailContent={buildDetailContentFromOrderItem(item)}
                    />
                    {/* Note per active order item (only if PENDING or WAITING) */}
                    {['PENDING', 'WAITING', ''].includes(statusKey) && (
                      <div className="pl-1 pt-1">
                        <button
                          type="button"
                          onClick={() => {
                            if (openNoteKey === item.id) {
                              setOpenNoteKey(null);
                            } else {
                              setOpenNoteKey(item.id);
                              setEditingNoteValue(item.notes ?? '');
                            }
                          }}
                          className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-medium transition ${
                            item.notes
                              ? 'border-amber-400 bg-amber-50 text-amber-700'
                              : 'border-divider text-text-muted hover:bg-surface-muted'
                          }`}
                          aria-label={item.notes ? 'Editar nota' : 'Añadir nota'}
                        >
                          <MessageSquare size={11} />
                          {item.notes ? 'Nota: ' + item.notes.slice(0, 20) + (item.notes.length > 20 ? '…' : '') : 'Nota'}
                        </button>
                        {openNoteKey === item.id && (
                          <div className="mt-1.5 space-y-1.5">
                            <div className="flex items-center gap-2">
                              <input
                                type="text"
                                autoFocus
                                value={editingNoteValue}
                                onChange={e => setEditingNoteValue(e.target.value)}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') {
                                    handleUpdateActiveItemNote(item.id, editingNoteValue);
                                    setOpenNoteKey(null);
                                  } else if (e.key === 'Escape') {
                                    setOpenNoteKey(null);
                                  }
                                }}
                                placeholder="Ej: sin sal, extra picante…"
                                maxLength={120}
                                className="flex-1 rounded-xl border border-divider bg-surface px-3 py-1.5 text-xs text-text placeholder:text-slate-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
                              />
                              <VoiceDictationButton
                                size="xs"
                                onTranscript={(text) =>
                                  setEditingNoteValue((prev) => (prev ? `${prev}, ${text}` : text))
                                }
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  handleUpdateActiveItemNote(item.id, editingNoteValue);
                                  setOpenNoteKey(null);
                                }}
                                className="text-xs font-semibold text-primary hover:underline"
                              >
                                OK
                              </button>
                              {item.notes && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    handleUpdateActiveItemNote(item.id, '');
                                    setEditingNoteValue('');
                                    setOpenNoteKey(null);
                                  }}
                                  className="text-xs text-red-500 hover:underline"
                                >
                                  Quitar
                                </button>
                              )}
                            </div>
                            <QuickNoteChips
                              value={editingNoteValue}
                              onChange={setEditingNoteValue}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
            {totals && (
              <OrderSummaryFooter
                subtotal={totals.subtotal}
                total={totals.total}
                className="mt-3"
              />
            )}
            <div className="flex flex-col gap-2 pt-2">
              <button
                onClick={handleSendPending}
                disabled={!hasPendingItems || sendToKitchenMutationIsPending}
                className={`w-full h-12 flex items-center justify-center gap-2 rounded-full font-bold text-sm transition-all duration-300 active:scale-[0.98] ${
                  hasPendingItems && !sendToKitchenMutationIsPending
                    ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-md shadow-orange-500/20 hover:from-orange-650 hover:to-amber-650'
                    : 'bg-slate-100 text-slate-400 border border-divider dark:bg-neutral-800 dark:text-neutral-500'
                }`}
                aria-label="Enviar a cocina"
              >
                {sendToKitchenMutationIsPending ? (
                  'Enviando...'
                ) : (
                  <>
                    <span>Enviar a cocina</span>
                    {hasPendingItems && (
                      <span className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-white px-1.5 text-[10px] font-extrabold text-orange-600 shadow-sm animate-bounce">
                        {pendingItemsCount}
                      </span>
                    )}
                  </>
                )}
              </button>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => printPreReceipt()}
                  disabled={
                    activeOrderItems.length === 0 ||
                    !activeOrder ||
                    activeOrder.status === 'CANCELLED'
                  }
                  className="inline-flex h-12 items-center justify-center gap-2 rounded-full border border-sky-400/50 bg-sky-50 text-sky-700 dark:bg-sky-950/40 dark:border-sky-700/60 dark:text-sky-300 px-3 text-sm font-bold shadow-sm transition-all duration-200 hover:bg-sky-100 hover:border-sky-500 hover:shadow active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-40 disabled:border-slate-200 dark:disabled:border-neutral-800 disabled:text-slate-400"
                  aria-label="Imprimir pre-cuenta"
                  title="Imprimir pre-cuenta para llevar a la mesa"
                >
                  <Receipt size={18} aria-hidden="true" /> Pre-cuenta
                </button>
                <button
                  onClick={handlePayClick}
                  disabled={
                    activeOrderItems.length === 0 ||
                    !activeOrder ||
                    activeOrder.payment_status === 'PAID' ||
                    activeOrder.status === 'CANCELLED'
                  }
                  className="inline-flex h-12 items-center justify-center gap-2 rounded-full bg-gradient-to-r from-emerald-600 to-teal-600 px-3 text-sm font-bold text-white shadow-md shadow-emerald-500/20 transition-all duration-300 hover:from-emerald-500 hover:to-teal-500 hover:shadow-lg hover:shadow-emerald-500/30 disabled:cursor-not-allowed disabled:from-slate-200 disabled:to-slate-200 disabled:text-slate-400 disabled:shadow-none dark:disabled:from-neutral-800 dark:disabled:to-neutral-800 dark:disabled:text-neutral-500 active:scale-[0.98]"
                  aria-label="Cobrar orden"
                >
                  <DollarSign size={18} aria-hidden="true" />
                  <span className="truncate">
                    {tableOrders.length > 1
                      ? `Cobrar Cuenta ${activeOrderAccountIndex || 1}`
                      : 'Cobrar'}
                  </span>
                </button>
              </div>
              {lastReceipt && activeOrder?.payment_status === 'PAID' && (
                <button
                  type="button"
                  onClick={() => setReceiptToPrint(lastReceipt)}
                  className="inline-flex h-10 items-center justify-center gap-2 rounded-full border border-divider px-4 text-xs font-semibold text-text-muted hover:bg-surface-muted transition"
                  aria-label="Reimprimir ticket"
                >
                  <Printer size={14} aria-hidden="true" /> Reimprimir
                </button>
              )}
            </div>
          </section>
        </div>
      ) : null}

      {!hasActiveOrder && hasDraft ? (
        <section className="space-y-3 rounded-2xl border border-divider bg-surface px-4 py-4 shadow-sm">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-[color:var(--color-text)]">
              {`Nueva orden #${nextOrderSequence}`}
            </h3>
            {highestKnownSequence > 0 && (
              <span className="text-xs text-muted">
                Se enviará como ticket independiente
              </span>
            )}
          </div>
          <ul className="divide-y divide-[color:var(--color-border)]/60" role="list">
            {Object.entries(cart).map(([key, item]) => {
              const mode =
                item.serveMode ??
                (draftFulfillment === 'TAKEOUT' ? 'TAKEAWAY' : 'MESA');
              return (
                <li key={key} className="py-1.5 px-2 -mx-2 hover:bg-slate-50/70 dark:hover:bg-neutral-800/20 rounded-2xl transition-all duration-200">
                  <ItemRow
                    id={key}
                    quantity={item.qty}
                    name={item.product.name}
                    unitPrice={
                      item.qty > 0
                        ? (item.linePriceCalc?.final ?? item.product.price * item.qty) / item.qty
                        : item.product.price
                    }
                    meta={[
                      (
                        <div key="mode-toggle" className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => handleCartItemServeMode(key, 'MESA')}
                            className={`rounded-full border px-3 py-1 text-[11px] font-medium transition ${mode === 'MESA'
                              ? 'border-orange-500 bg-orange-500 text-white'
                              : 'border-divider text-[color:var(--color-text)] hover:bg-surface-muted'
                              }`}
                            aria-pressed={mode === 'MESA'}
                          >
                            En mesa
                          </button>
                          <button
                            type="button"
                            onClick={() => handleCartItemServeMode(key, 'TAKEAWAY')}
                            className={`rounded-full border px-3 py-1 text-[11px] font-medium transition ${mode === 'TAKEAWAY'
                              ? 'border-blue-500 bg-blue-600 text-white'
                              : 'border-divider text-[color:var(--color-text)] hover:bg-surface-muted'
                              }`}
                            aria-pressed={mode === 'TAKEAWAY'}
                          >
                            Para llevar
                          </button>
                        </div>
                      ),
                      item.product.additions?.enabled || item.product.exceptions?.enabled ? (
                        <button
                          key="options"
                          type="button"
                          onClick={() => setOptionsKey(key)}
                          className="inline-flex items-center rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 dark:bg-neutral-800 dark:text-neutral-355 px-2.5 py-1 text-[10px] font-extrabold tracking-wider transition-all uppercase border border-divider/40"
                          aria-label="Editar opciones"
                        >
                          Opciones
                        </button>
                      ) : null,
                    ].filter(Boolean)}
                    detailContent={buildDetailContentFromCartItem(item)}
                    onIncrease={() => changeQty(key, 1)}
                    onDecrease={() => changeQty(key, -1)}
                    onRemove={() => removeCartItem(key)}
                    disableDecrease={item.qty <= 1}
                  />
                  {/* Note per item */}
                  <div className="pl-1 pt-1">
                    <button
                      type="button"
                      onClick={() => setOpenNoteKey(openNoteKey === key ? null : key)}
                      className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-medium transition ${
                        item.note
                          ? 'border-amber-400 bg-amber-50 text-amber-700'
                          : 'border-divider text-text-muted hover:bg-surface-muted'
                      }`}
                      aria-label={item.note ? 'Editar nota' : 'Añadir nota'}
                    >
                      <MessageSquare size={11} />
                      {item.note ? 'Nota: ' + item.note.slice(0, 20) + (item.note.length > 20 ? '…' : '') : 'Nota'}
                    </button>
                    {openNoteKey === key && (
                      <div className="mt-1.5 space-y-1.5">
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            autoFocus
                            value={item.note ?? ''}
                            onChange={e => setItemNote(key, e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter' || e.key === 'Escape') setOpenNoteKey(null); }}
                            placeholder="Ej: sin sal, extra picante…"
                            maxLength={120}
                            className="flex-1 rounded-xl border border-divider bg-surface px-3 py-1.5 text-xs text-text placeholder:text-slate-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
                          />
                          <VoiceDictationButton
                            size="xs"
                            onTranscript={(text) =>
                              setItemNote(key, item.note ? `${item.note}, ${text}` : text)
                            }
                          />
                          <button
                            type="button"
                            onClick={() => setOpenNoteKey(null)}
                            className="text-xs font-semibold text-primary hover:underline"
                          >
                            OK
                          </button>
                          {item.note && (
                            <button
                              type="button"
                              onClick={() => { setItemNote(key, ''); setOpenNoteKey(null); }}
                              className="text-xs text-red-500 hover:underline"
                            >
                              Quitar
                            </button>
                          )}
                        </div>
                        <QuickNoteChips
                          value={item.note ?? ''}
                          onChange={newNote => setItemNote(key, newNote)}
                        />
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>

          {totals && (
            <OrderSummaryFooter
              subtotal={totals.subtotal}
              total={totals.total}
              className="mt-3"
            />
          )}
          <div className="grid grid-cols-[1fr_2fr] gap-2 pt-2">
            <button
              onClick={() => setCart({})}
              className="btn btn-outline px-3 border-red-200 text-red-650 hover:bg-red-50 hover:border-red-350 dark:border-red-900/30 dark:text-red-400 dark:hover:bg-red-950/20 transition-all duration-200 active:scale-95"
              aria-label="Vaciar pedido"
            >
              Vaciar
            </button>
            <button
              onClick={handleOrder}
              disabled={hasFlavorErrors || createOrderIsPending}
              className="btn btn-primary w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white shadow-md shadow-blue-500/10 hover:shadow-lg transition-all duration-300 active:scale-[0.98] flex items-center justify-center gap-2"
              aria-label="Ordenar"
            >
              <HandPlatter size={18} aria-hidden="true" />
              {createOrderIsPending ? 'Creando...' : 'Ordenar'}
            </button>
          </div>
          {hasFlavorErrors && (
            <p className="text-center text-xs text-red-500">
              Selecciona al menos un sabor para los productos requeridos.
            </p>
          )}
        </section>
      ) : null}

      {!hasActiveOrder && !hasDraft ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-divider bg-surface/50 px-4 py-8 text-center transition-all">
          <style dangerouslySetInnerHTML={{__html: `
            @keyframes float {
              0%, 100% { transform: translateY(0); }
              50% { transform: translateY(-6px); }
            }
            .animate-float {
              animation: float 2.5s ease-in-out infinite;
            }
            @keyframes pop {
              0% { transform: scale(0.9); }
              50% { transform: scale(1.15); }
              100% { transform: scale(1); }
            }
            .animate-pop {
              animation: pop 0.25s cubic-bezier(0.175, 0.885, 0.32, 1.275);
            }
          `}} />
          <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 dark:bg-neutral-800 text-slate-400 dark:text-neutral-500 animate-float">
            <HandPlatter size={22} className="stroke-[1.5]" />
          </div>
          <p className="text-sm font-semibold text-text-muted">Pedido vacío</p>
          <p className="mt-1 text-xs text-muted max-w-[200px]">
            Agrega productos para crear una orden nueva.
          </p>
        </div>
      ) : null}

      {/* Modales de Dividir / Cancelar (controlados por el panel) */}
      {splitModalOpen && (
        <SplitOrderModal
          open={splitModalOpen}
          order={activeOrder}
          loading={splitOrderMutation?.isPending}
          onClose={() => setSplitModalOpen(false)}
          onConfirm={(itemIds: any) => {
            if (activeOrder) splitOrderMutation?.mutate({ orderId: activeOrder.id, itemIds });
            setSplitModalOpen(false);
          }}
        />
      )}

      {cancelModalOpen && (
        <ConfirmCancelOrderModal
          open={cancelModalOpen}
          order={activeOrder}
          loading={cancelOrderMutation?.isPending}
          onClose={() => setCancelModalOpen(false)}
          onConfirm={() => {
            handleCancelActiveOrder();
            setCancelModalOpen(false);
          }}
        />
      )}

      {transferModalOpen && (
        <TransferTableModal
          open={transferModalOpen}
          tables={tables || []}
          currentTable={selected}
          onClose={() => setTransferModalOpen(false)}
          onConfirm={(targetTableId) => {
            if (activeOrder) {
              transferOrderTableMutation.mutate({
                orderId: activeOrder.id,
                tableId: targetTableId,
              });
            }
            setTransferModalOpen(false);
          }}
          loading={transferOrderTableMutation.isPending}
        />
      )}
    </div>
  );
}
